import ICAL from 'ical.js';

// Requires a CORS proxy because iCloud/Google ICS links don't usually send CORS headers for browser fetch
const DEFAULT_PROXY = 'https://corsproxy.io/?';

/**
 * Fetches and parses ICS data, expanding recurring events within the given range.
 * @param {string} icsUrl - The WebCal/ICS URL
 * @param {Date|string} rangeStart - Start of the view range (inclusive)
 * @param {Date|string} rangeEnd - End of the view range (inclusive)
 * @param {string} proxyUrl - CORS Proxy URL
 * @param {string} calendarName - Name for display
 * @param {string} color - Color for display
 */
export const fetchIcsEvents = async (icsUrl, rangeStart, rangeEnd, proxyUrl = DEFAULT_PROXY, calendarName = 'Apple Calendar', color = '#ef4444') => {
  try {
    // 1. Prepare URL
    let targetUrl = icsUrl.replace('webcal://', 'https://');
    const fetchUrl = `${proxyUrl}${encodeURIComponent(targetUrl)}`;

    // 2. Fetch Data
    const response = await fetch(fetchUrl);
    if (!response.ok) throw new Error(`Failed to fetch ICS: ${response.statusText}`);
    const icsData = await response.text();

    // 3. Parse ICS
    const jcalData = ICAL.parse(icsData);
    const comp = new ICAL.Component(jcalData);
    
    // 4. Handle Timezones (Basic)
    // ICAL.js handles timezones internally mostly, but we ensure we work with JS Dates
    
    // 5. Expand Events
    const events = [];
    const vevents = comp.getAllSubcomponents('vevent');
    
    // Convert range strings to ICAL.Time if needed, or JS Date
    const start = new ICAL.Time.fromJSDate(new Date(rangeStart));
    const end = new ICAL.Time.fromJSDate(new Date(rangeEnd));

    vevents.forEach(vevent => {
      const event = new ICAL.Event(vevent);
      
      // Handle Recurrence
      if (event.isRecurring()) {
        const iterator = event.iterator();
        let next;
        
        // Iterate through occurrences
        while ((next = iterator.next())) {
          // If occurrence is before our start range, skip (but keep iterating if it might fall inside later)
          // Optimization: If the start of the occurrence is after our end range, we can stop
          if (next.compare(end) > 0) break;
          
          if (next.compare(start) >= 0) {
            // It's inside our range (or starts after rangeStart)
            const details = event.getOccurrenceDetails(next);
            events.push(formatIcalEvent(details.item, details.startDate, details.endDate, icsUrl, calendarName, color));
          }
        }
      } else {
        // Single Event
        // Check if it falls within range
        const eStart = event.startDate;
        // Simple check: if event end is after range start AND event start is before range end
        if (event.endDate.compare(start) > 0 && event.startDate.compare(end) < 0) {
            events.push(formatIcalEvent(event, event.startDate, event.endDate, icsUrl, calendarName, color));
        }
      }
    });

    return events;

  } catch (error) {
    console.error("Error parsing ICS", error);
    // Return empty array on error to not crash the calendar
    return [];
  }
};

const formatIcalEvent = (event, startDate, endDate, sourceId, calendarName, color) => {
    return {
        id: `${event.uid}_${startDate.toICALString()}`, // Unique ID for occurrence
        title: event.summary || 'Ohne Titel',
        description: event.description,
        start: startDate.toJSDate(),
        end: endDate.toJSDate(),
        allDay: startDate.isDate, // isDate means no time component -> All Day
        location: event.location,
        source: 'apple',
        calendarId: sourceId,
        calendarName: calendarName,
        color: color,
        originalEvent: event
    };
};